import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import passwordService, { PasswordListItem } from '../../services/passwordService';
import { getApiErrorMessage } from '../../utils/apiError';

const CLAVE_MIN = 12;

const Passwords: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [items, setItems] = useState<PasswordListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formId, setFormId] = useState('');
  const [formClave, setFormClave] = useState('');
  const [formClaveConfirm, setFormClaveConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(
    async (pageNum = page, pageSize = rowsPerPage, search = searchTerm) => {
      try {
        setLoading(true);
        const data = await passwordService.getPaged({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
        });
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar contraseñas'), {
          variant: 'error',
        });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchTerm, enqueueSnackbar],
  );

  useEffect(() => {
    void fetchItems();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      void fetchItems(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openCreate = () => {
    setEditingId(null);
    setFormId('');
    setFormClave('');
    setFormClaveConfirm('');
    setDialogOpen(true);
  };

  const openEdit = (row: PasswordListItem) => {
    setEditingId(row.idPassword);
    setFormId(row.idPassword);
    setFormClave('');
    setFormClaveConfirm('');
    setDialogOpen(true);
  };

  const validateForm = (): string | null => {
    if (!editingId) {
      const id = formId.trim();
      if (!id) return 'Indicá el Id de contraseña.';
      if (id.length > 20) return 'El Id no puede superar 20 caracteres.';
    }
    if (!formClave || formClave.length < CLAVE_MIN) {
      return `La clave debe tener al menos ${CLAVE_MIN} caracteres.`;
    }
    if (formClave !== formClaveConfirm) return 'Las contraseñas no coinciden.';
    return null;
  };

  const handleSave = async () => {
    const errorMsg = validateForm();
    if (errorMsg) {
      enqueueSnackbar(errorMsg, { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await passwordService.update(editingId, formClave);
        enqueueSnackbar('Contraseña actualizada', { variant: 'success' });
      } else {
        await passwordService.create(formId.trim(), formClave);
        enqueueSnackbar('Contraseña creada', { variant: 'success' });
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo guardar la contraseña'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: PasswordListItem) => {
    if (!window.confirm(`¿Eliminar la contraseña «${row.idPassword}»?`)) return;
    try {
      await passwordService.remove(row.idPassword);
      enqueueSnackbar('Contraseña eliminada', { variant: 'success' });
      await fetchItems();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo eliminar'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Contraseñas de autorización
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Catálogo Passwords (hasheadas). Asigná el Id en Tipos de formulario por Sucursal
                (PassAnulacion / PassEmision).
              </Typography>
            </Box>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
              Nueva contraseña
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por Id..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' },
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {items.map((row) => (
                <Card key={row.idPassword} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {row.idPassword}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.tieneClave ? 'Clave configurada (hash)' : 'Sin clave'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => void handleDelete(row)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
              {!loading && items.length === 0 && (
                <Typography color="text.secondary">No hay contraseñas.</Typography>
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Id</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={row.idPassword} hover>
                      <TableCell>{row.idPassword}</TableCell>
                      <TableCell>
                        {row.tieneClave ? 'Clave configurada (hash)' : 'Sin clave'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Cambiar clave">
                          <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void handleDelete(row)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="text.secondary">No hay contraseñas.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => {
                  setPage(newPage);
                  void fetchItems(newPage, rowsPerPage, searchTerm);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  const size = parseInt(e.target.value, 10);
                  setRowsPerPage(size);
                  setPage(0);
                  void fetchItems(0, size, searchTerm);
                }}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Cambiar clave' : 'Nueva contraseña'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Id"
              value={formId}
              onChange={(e) => setFormId(e.target.value)}
              disabled={!!editingId}
              fullWidth
              inputProps={{ maxLength: 20 }}
              helperText="Identificador de hasta 20 caracteres (ej. ANULAR_REMITOS)."
            />
            <TextField
              label="Clave"
              type="password"
              value={formClave}
              onChange={(e) => setFormClave(e.target.value)}
              fullWidth
              autoComplete="new-password"
              helperText={`Mínimo ${CLAVE_MIN} caracteres. Se guarda hasheada.`}
            />
            <TextField
              label="Confirmar clave"
              type="password"
              value={formClaveConfirm}
              onChange={(e) => setFormClaveConfirm(e.target.value)}
              fullWidth
              autoComplete="new-password"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Passwords;
